import { useSoftphone } from "@dialstack/sdk-react/softphone";
import { useEffect, useMemo, useRef, useState } from "react";

import type { Call, CallEndReason } from "@dialstack/sdk-webrtc";

import { useAppDispatch, useAppSelector } from "app/hooks";

import {
  type BrowserLegEnd,
  DEVICE_CALL_MAX_AGE_MS,
  HANDSET_ANSWER_GRACE_MS,
  type InboundCallStatus,
  browserLegEnded,
  selectBrowserLegEnds,
  selectDeclinedCallIds,
  selectDismissedDeviceCallIds,
  selectInboundCallStatuses
} from "../device-call.slice";
import type { IncomingCallPop } from "../incoming-call-pop.slice";
import { selectMissedCallFrames } from "../missed-calls.slice";
import { useDialstackSoftphone } from "./use-dialstack-softphone.hook";
import { type InboundCallMatch, isRinging } from "./use-inbound-call-pops.hook";

export type DeviceCallPhase = "connecting" | "active" | "ended";

// An inbound call this user took somewhere other than this browser — their
// cordless handset, desk phone or mobile — that the widget keeps a card open
// for (ENG-2562, ENG-2605). "connecting" while the browser's ringing leg has
// just ended and nothing has said yet where the call went; "active" once the
// backend named this user as the answerer; "ended" once it reported the end.
export type DeviceCall = {
  callId: string;
  backendCallId: number | null;
  clinicId: number | null;
  pop: IncomingCallPop | null;
  phase: DeviceCallPhase;
  // Epoch ms, for the timer: the provider's connected_at when the frame has
  // it, else when the frame arrived. Null while connecting.
  answeredAt: number | null;
  endedAt: number | null;
  callStatus: string | null;
};

const answeredHere = (call: InboundCallMatch["call"]) =>
  call.state === "active" || call.state === "held";

type SeenLeg = Omit<BrowserLegEnd, "callId" | "endedAt" | "endReason">;

// The SDK's lists only show a call gone; the call object itself says why it
// ended ("hangup" when the server dropped the leg). The hook's fakes in
// tests, and any future call shape without the emitter, are left alone.
function recordEndReason(
  call: InboundCallMatch["call"],
  onEnd: (reason: CallEndReason) => void
) {
  const emitter = call as Partial<Pick<Call, "on">>;
  if (typeof emitter.on === "function") emitter.on("ended", onEnd);
}

// Remembers how each of this browser's inbound legs ended: the pop it was
// joined to, whether it was answered on this computer, whether it was still
// ringing, and the SDK's reason. Mounted once by the widget. A leg that
// leaves the SDK's lists still ringing was taken elsewhere, by somebody
// else, or abandoned — the status frame (or a missed-call frame) says which
// — and a leg that was active here is this browser's own call, so a status
// frame for it is noise.
export function useDeviceCallTracker(matches: InboundCallMatch[]) {
  const dispatch = useAppDispatch();
  const seen = useRef(new Map<string, SeenLeg>());
  const reasons = useRef(new Map<string, CallEndReason>());
  // Keyed on what can change the record, not on the match objects (rebuilt
  // every render).
  const key = matches
    .map(
      (match) => `${match.callId}:${match.call.state}:${match.backendCallId}`
    )
    .join("|");
  useEffect(() => {
    const live = new Set<string>();
    for (const match of matches) {
      live.add(match.callId);
      const previous = seen.current.get(match.callId);
      if (!previous)
        recordEndReason(match.call, (reason) =>
          reasons.current.set(match.callId, reason)
        );
      seen.current.set(match.callId, {
        pop: match.pop ?? previous?.pop ?? null,
        backendCallId: match.backendCallId ?? previous?.backendCallId ?? null,
        answeredHere:
          (previous?.answeredHere ?? false) || answeredHere(match.call),
        endedRinging: isRinging(match.call)
      });
    }
    for (const [callId, leg] of [...seen.current.entries()]) {
      if (live.has(callId)) continue;
      seen.current.delete(callId);
      const endReason = reasons.current.get(callId) ?? null;
      reasons.current.delete(callId);
      dispatch(
        browserLegEnded({ callId, ...leg, endReason, endedAt: Date.now() })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, dispatch]);
}

const toDeviceCall = (
  status: InboundCallStatus,
  leg: BrowserLegEnd | undefined,
  pop: IncomingCallPop | null
): DeviceCall => ({
  callId: status.callId,
  backendCallId: status.backendCallId ?? leg?.backendCallId ?? null,
  clinicId: status.clinicId ?? pop?.clinicId ?? null,
  pop,
  phase: status.event === "ended" ? "ended" : "active",
  answeredAt: status.answeredAt
    ? Date.parse(status.answeredAt)
    : status.receivedAt,
  endedAt:
    status.event === "ended"
      ? status.endedAt
        ? Date.parse(status.endedAt)
        : status.receivedAt
      : null,
  callStatus: status.callStatus
});

const connectingCall = (leg: BrowserLegEnd): DeviceCall => ({
  callId: leg.callId,
  backendCallId: leg.backendCallId,
  clinicId: leg.pop?.clinicId ?? null,
  pop: leg.pop,
  phase: "connecting",
  answeredAt: null,
  endedAt: null,
  callStatus: null
});

// A ringing leg the server hung up (not declined here, not timed out) may be
// this user's handset picking up: hold the card for the grace period unless
// the backend has since said the caller gave up.
const mayBeHandsetAnswer = (leg: BrowserLegEnd) =>
  leg.endedRinging &&
  !leg.answeredHere &&
  (leg.endReason === null || leg.endReason === "hangup");

// The call this user is on (or just finished) away from this browser, if
// any: a status frame named them as the answerer, the SDK does not carry the
// call, and this browser never answered it — or, before any frame, a leg that
// just stopped ringing and may be their handset picking up. Only the tab that
// holds the softphone decides — a standby tab has no SDK calls at all, so for
// it every call the user answers in the other tab would look like a handset
// call.
export default function useDeviceCall(): DeviceCall | null {
  const { enabled } = useDialstackSoftphone();
  const { incomingCalls, calls } = useSoftphone();
  const statuses = useAppSelector(selectInboundCallStatuses);
  const legs = useAppSelector(selectBrowserLegEnds);
  const dismissed = useAppSelector(selectDismissedDeviceCallIds);
  const declined = useAppSelector(selectDeclinedCallIds);
  const missed = useAppSelector(selectMissedCallFrames);
  const pops = useAppSelector((state) => state.incomingCallPop.pops);
  const liveKey = [...incomingCalls, ...calls].map((call) => call.id).join("|");
  // The connecting hold runs out on its own; this re-evaluates when it does.
  const [tick, setTick] = useState(0);

  const deviceCall = useMemo(() => {
    if (!enabled) return null;
    const live = new Set(liveKey.split("|").filter(Boolean));
    const dismissedIds = new Set(dismissed);
    const declinedIds = new Set(declined);
    const now = Date.now();
    const status = Object.values(statuses)
      .filter(
        (candidate) =>
          !live.has(candidate.callId) &&
          !dismissedIds.has(candidate.callId) &&
          legs[candidate.callId]?.answeredHere !== true &&
          now - candidate.receivedAt <= DEVICE_CALL_MAX_AGE_MS
      )
      .sort((a, b) => b.receivedAt - a.receivedAt)[0];
    if (status) {
      const leg = legs[status.callId];
      // The pop the leg was joined to; else the stored pop by id, whatever
      // its age — a handset call outlives the pop's ring-time TTL.
      const pop =
        leg?.pop ??
        pops.find((candidate) => candidate.callId === status.callId) ??
        null;
      return toDeviceCall(status, leg, pop);
    }
    const abandoned = new Set(missed.map((frame) => frame.providerCallId));
    const pending = Object.values(legs)
      .filter(
        (leg) =>
          mayBeHandsetAnswer(leg) &&
          !live.has(leg.callId) &&
          !(leg.callId in statuses) &&
          !abandoned.has(leg.callId) &&
          !declinedIds.has(leg.callId) &&
          !dismissedIds.has(leg.callId) &&
          now - leg.endedAt <= HANDSET_ANSWER_GRACE_MS
      )
      .sort((a, b) => b.endedAt - a.endedAt)[0];
    return pending ? connectingCall(pending) : null;
    // `tick` only forces the grace check to run again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    liveKey,
    statuses,
    legs,
    dismissed,
    declined,
    missed,
    pops,
    tick
  ]);

  const connectingId =
    deviceCall?.phase === "connecting" ? deviceCall.callId : null;
  useEffect(() => {
    if (connectingId === null) return undefined;
    const endedAt = legs[connectingId]?.endedAt ?? Date.now();
    const timer = window.setTimeout(
      () => setTick((count) => count + 1),
      Math.max(0, endedAt + HANDSET_ANSWER_GRACE_MS - Date.now()) + 1
    );
    return () => window.clearTimeout(timer);
  }, [connectingId, legs]);

  return deviceCall;
}
